import { createFeatureSelector, createSelector } from '@ngrx/store';

import { isDefined } from '@shared/utils/functions';

import { CapacityActions } from './capacity.actions';
import { FeatureState, featureKey } from './capacity.reducer';

export const selectFeature = createFeatureSelector<FeatureState>(featureKey);

const selectIsLoading$ = createSelector(selectFeature, ({ isLoading }) => isLoading);
const selectIsOverviewLoading$ = createSelector(
  selectFeature,
  ({ isLoading }) => isLoading[CapacityActions.getCapacityGraph.type] || isLoading[CapacityActions.getNtcOverview.type]
);
const selectIsPlannedOutageCreateLoading$ = createSelector(
  selectFeature,
  ({ isLoading }) => isLoading[CapacityActions.createNtcPlannedOutage.type] || isLoading[CapacityActions.getNtcPlannedOutageOptions.type]
);
const selectIsPlannedOutageEditLoading$ = createSelector(
  selectFeature,
  ({ isLoading }) =>
    isLoading[CapacityActions.deleteNtcPlannedOutage.type] ||
    isLoading[CapacityActions.getNtcPlannedOutage.type] ||
    isLoading[CapacityActions.updateNtcPlannedOutage.type] ||
    isLoading[CapacityActions.getNtcPlannedOutageOptions.type]
);
const selectIsPlannedOutagesLoading$ = createSelector(
  selectFeature,
  ({ isLoading }) =>
    isLoading[CapacityActions.cancelNtcPlannedOutage.type] ||
    isLoading[CapacityActions.deleteNtcPlannedOutage.type] ||
    isLoading[CapacityActions.getNextNtcPlannedOutages.type] ||
    isLoading[CapacityActions.getNtcPlannedOutages.type]
);
const selectIsUnplannedOutageCreateLoading$ = createSelector(
  selectFeature,
  ({ isLoading }) =>
    isLoading[CapacityActions.createNtcUnplannedOutage.type] || isLoading[CapacityActions.getNtcUnplannedOutageOptions.type]
);
const selectIsUnplannedOutageEditLoading$ = createSelector(
  selectFeature,
  ({ isLoading }) =>
    isLoading[CapacityActions.getNtcUnplannedOutage.type] ||
    isLoading[CapacityActions.updateNtcUnplannedOutage.type] ||
    isLoading[CapacityActions.getNtcUnplannedOutageOptions.type]
);
const selectIsUnplannedOutagesLoading$ = createSelector(
  selectFeature,
  ({ isLoading }) => isLoading[CapacityActions.getNextNtcUnplannedOutages.type] || isLoading[CapacityActions.getNtcUnplannedOutages.type]
);
const selectOverview$ = createSelector(selectFeature, ({ overview }) => overview);
const selectLastNtcOverviewRequestData$ = createSelector(selectFeature, ({ lastNtcOverviewRequestData }) => lastNtcOverviewRequestData);
const selectCapacityOverview$ = createSelector(selectFeature, ({ capacityOverview }) => capacityOverview);
const selectCapacityGraph$ = createSelector(selectFeature, ({ capacityGraph }) => capacityGraph);
const selectLastCapacityGraphRequestData$ = createSelector(
  selectFeature,
  ({ lastCapacityGraphRequestData }) => lastCapacityGraphRequestData
);
const selectIsGetAtcOverviewLoading$ = createSelector(selectFeature, ({ isLoading }) => isLoading[CapacityActions.getAtcOverview.type]);
const selectIsRefreshAtcOverviewLoading$ = createSelector(
  selectFeature,
  ({ isLoading }) => isLoading[CapacityActions.refreshAtcOverview.type]
);

const selectPlannedOutageOptions$ = createSelector(selectFeature, ({ plannedOutageOptions }) => plannedOutageOptions);
const selectPlannedOutages$ = createSelector(selectFeature, ({ plannedOutages }) => plannedOutages);
const selectHasNextPlannedOutagesElements$ = createSelector(selectPlannedOutages$, selectIsLoading$, (plannedOutages, isLoading) => {
  if (
    !isDefined(plannedOutages) ||
    !isDefined(plannedOutages.entries) ||
    !isDefined(plannedOutages.totalEntries) ||
    isLoading[CapacityActions.getNtcPlannedOutages.type] ||
    isLoading[CapacityActions.getNextNtcPlannedOutages.type]
  ) {
    return false;
  }

  return plannedOutages.totalEntries > plannedOutages.entries.length;
});

const selectPlannedOutage$ = createSelector(selectFeature, ({ plannedOutage }) => plannedOutage);
const selectUnplannedOutages$ = createSelector(selectFeature, ({ unplannedOutages }) => unplannedOutages);
const selectHasNextUnplannedOutagesElements$ = createSelector(selectUnplannedOutages$, selectIsLoading$, (unplannedOutages, isLoading) => {
  if (
    !isDefined(unplannedOutages) ||
    !isDefined(unplannedOutages.entries) ||
    !isDefined(unplannedOutages.totalEntries) ||
    isLoading[CapacityActions.getNtcUnplannedOutages.type] ||
    isLoading[CapacityActions.getNextNtcUnplannedOutages.type]
  ) {
    return false;
  }

  return unplannedOutages.totalEntries > unplannedOutages.entries.length;
});

const selectUnplannedOutageOptions$ = createSelector(selectFeature, ({ unplannedOutageOptions }) => unplannedOutageOptions);
const selectUnplannedOutage$ = createSelector(selectFeature, ({ unplannedOutage }) => unplannedOutage);

const selectNtc$ = createSelector(selectFeature, ({ ntc }) => ntc);
const selectNtcOptions$ = createSelector(selectFeature, ({ ntcOptions }) => ntcOptions);
const selectSubmitNtcError$ = createSelector(selectFeature, ({ submitNtcError }) => submitNtcError);

const selectAtc$ = createSelector(selectFeature, ({ atc }) => atc);
const selectSubmitAtcSuccess$ = createSelector(selectFeature, ({ submitAtcSuccess }) => submitAtcSuccess);
const selectSubmitAtcError$ = createSelector(selectFeature, ({ submitAtcError }) => submitAtcError);

const selectAtcOverview$ = createSelector(selectFeature, ({ atcOverview }) => atcOverview);
const selectLastAtcOverviewRequestData$ = createSelector(selectFeature, ({ lastAtcOverviewRequestData }) => lastAtcOverviewRequestData);

export const fromCapacity = {
  selectIsLoading$,
  selectIsOverviewLoading$,
  selectIsPlannedOutageCreateLoading$,
  selectIsPlannedOutageEditLoading$,
  selectIsPlannedOutagesLoading$,
  selectIsUnplannedOutageCreateLoading$,
  selectIsUnplannedOutageEditLoading$,
  selectIsUnplannedOutagesLoading$,
  selectOverview$,
  selectLastNtcOverviewRequestData$,
  selectLastAtcOverviewRequestData$,
  selectCapacityOverview$,
  selectCapacityGraph$,
  selectLastCapacityGraphRequestData$,
  selectPlannedOutageOptions$,
  selectPlannedOutages$,
  selectHasNextPlannedOutagesElements$,
  selectPlannedOutage$,
  selectUnplannedOutages$,
  selectHasNextUnplannedOutagesElements$,
  selectUnplannedOutageOptions$,
  selectUnplannedOutage$,
  selectNtc$,
  selectNtcOptions$,
  selectSubmitNtcError$,
  selectAtc$,
  selectSubmitAtcSuccess$,
  selectSubmitAtcError$,
  selectAtcOverview$,
  selectIsGetAtcOverviewLoading$,
  selectIsRefreshAtcOverviewLoading$,
};
